/* eslint-disable spire/export-styles */
import withDynamicGoogleMaps, { HasDynamicGoogleMaps } from "@insite/client-framework/Common/withDynamicGoogleMaps";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { parseWcTranslationCookie } from "@WhiteCap/Common/WcTranslationCookie";
import handleBranchSelection from "@WhiteCap/Handlers/BranchSelection/HandleBranchSelection";
import { getGeoFallbackBranch } from "@WhiteCap/Services/GeoFallbackBranchService";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    return {
        settings: getSettingsCollection(state),
        session: state.context.session,
    };
};

const mapDispatchToProps = {
    handleBranchSelection,
    updatePickUpWarehouse,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasDynamicGoogleMaps;

const LocationHelper = ({ useLoadScript, settings, handleBranchSelection, updatePickUpWarehouse, session }: Props) => {
    const { googleMapsApiKey } = settings.websiteSettings;
    const { isLoaded } = useLoadScript({ googleMapsApiKey });
    const toasterContext = React.useContext(ToasterContext);

    // Move applyGeoFallbackBranch outside useEffect so it can be used by getNearestWarehouse
    const applyGeoFallbackBranch = React.useCallback(() => {
        getGeoFallbackBranch()
            .then(apiResult => {
                const branch = apiResult?.nearestBranch;
                if (!branch?.id) {
                    return;
                }
                if (session.pickUpWarehouse?.id === branch.id) {
                    return;
                }
                updatePickUpWarehouse({
                    pickUpWarehouse: branch,
                    onSuccess: () => {
                        toasterContext.addToast({
                            body: siteMessage("geoLocation_Updated_To_Nearby_Branch"),
                            messageType: "success",
                        });
                    },
                });
            })
            .catch(() => {
                // Fallback endpoint unavailable; leave session branch unchanged.
            });
    }, [session.pickUpWarehouse?.id, updatePickUpWarehouse, toasterContext]);

    const getNearestWarehouse = React.useCallback(
        (latitude: number, longitude: number) => {
            handleBranchSelection({
                latitude,
                longitude,
                onSuccess: result => {
                    if (result && result?.nearestBranch !== undefined && result.nearestBranch?.id) {
                        if (session.pickUpWarehouse?.id === result?.nearestBranch?.id) {
                            return;
                        }
                        const isAutoChangeProperty = { isAutoChange: "true" };
                        updatePickUpWarehouse({
                            pickUpWarehouse: {
                                ...result.nearestBranch,
                                properties: isAutoChangeProperty,
                            },
                            onSuccess: () => {
                                toasterContext.addToast({
                                    body: siteMessage("geoLocation_Updated_To_Nearby_Branch"),
                                    messageType: "success",
                                });
                            },
                        });
                    } else {
                        applyGeoFallbackBranch();
                        //   toasterContext.addToast({
                        //       body: siteMessage("geoLocation_No_Nearby_Branch_Found"),
                        //       messageType: "info",
                        //   });
                    }
                },
            });
        },
        [
            handleBranchSelection,
            session.pickUpWarehouse?.id,
            updatePickUpWarehouse,
            toasterContext,
            applyGeoFallbackBranch,
        ],
    );

    const setPreviousBranchAsCurrentBranch = React.useCallback(
        (alreadySelectedBranch: WarehouseModel) => {
            updatePickUpWarehouse({
                pickUpWarehouse: alreadySelectedBranch,
                onSuccess: result => {
                    toasterContext.addToast({
                        body: siteMessage("geoLocation_Updated_Previously_Selected_Branch"),
                        messageType: "success",
                    });
                },
            });
        },
        [updatePickUpWarehouse, toasterContext],
    );

    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        // CRITICAL: If translation cookie exists, skip ALL location logic
        // The cookie sync is responsible for cross-platform consistency
        const translationCookie = parseWcTranslationCookie();
        if (translationCookie?.branchId) {
            localStorage.removeItem("selectedShipBranch");
            return;
        }

        // Check if sign-in geolocation is in progress
        const signInGeolocationInProgress = sessionStorage.getItem("signInGeolocationInProgress");
        if (signInGeolocationInProgress === "true") {
            return; // Wait for sign-in geolocation to complete
        }

        // Skip if warehouse was already set by sign-in geolocation
        const warehouseJustSetBySignIn = sessionStorage.getItem("warehouseSetBySignIn");
        if (warehouseJustSetBySignIn === "true") {
            sessionStorage.removeItem("warehouseSetBySignIn");
            if (session?.pickUpWarehouse?.id !== undefined) {
                localStorage.setItem("selectedShipBranch", JSON.stringify(session?.pickUpWarehouse));
            }
            return;
        }

        //  Check if geolocation is required from session properties OR sessionStorage
        const sessionStorageFlag = sessionStorage.getItem("isGeoLocateRequired");
        const shouldPerformGeolocation =
            session.properties?.isGeoLocateRequired?.toLowerCase() === "true" ||
            sessionStorageFlag?.toLowerCase() === "true";

        if (shouldPerformGeolocation) {
            if (!navigator.geolocation) {
                applyGeoFallbackBranch();
                sessionStorage.removeItem("isGeoLocateRequired");
                return;
            }

            navigator.geolocation.getCurrentPosition(
                position => {
                    const { latitude, longitude } = position.coords;
                    getNearestWarehouse(latitude, longitude);
                    sessionStorage.removeItem("isGeoLocateRequired");
                },
                () => {
                    applyGeoFallbackBranch();
                    sessionStorage.removeItem("isGeoLocateRequired");
                },
            );
        } else {
            // Geolocation NOT required - restore from localStorage ONLY if no warehouse is set
            // If session already has a warehouse, don't restore from localStorage
            if (session?.pickUpWarehouse?.id) {
                return;
            }

            const alreadySelectedBranch = localStorage.getItem("selectedShipBranch");
            if (alreadySelectedBranch !== null && alreadySelectedBranch !== "") {
                const alreadySelectedBranchModel: WarehouseModel = JSON.parse(alreadySelectedBranch);
                if (alreadySelectedBranchModel && alreadySelectedBranchModel?.id !== session?.pickUpWarehouse?.id) {
                    setPreviousBranchAsCurrentBranch(alreadySelectedBranchModel);
                }
            }
        }
    }, [isLoaded, session, applyGeoFallbackBranch, getNearestWarehouse, setPreviousBranchAsCurrentBranch]);

    return <></>;
};

export default connect(mapStateToProps, mapDispatchToProps)(withDynamicGoogleMaps(LocationHelper));
